import type { Instruction, LessonBundle, MediaRef } from '@ezishizi/content-schema';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { audio, wentSilent, type PlayOutcome } from '../lib/audio.ts';
import { sfx } from '../lib/sfx.ts';
import type { PinyinMode } from '../lib/store/types.ts';
import { Shizi } from './mascot/Shizi.tsx';
import { SpeakerIcon, StarIcon } from './icons.tsx';

/** An illustration sized by the surrounding font-size. One cache-busting retry if the first load
 * fails (e.g. a poisoned cache entry), so a card never silently shows an empty box. */
function UrlPicture({ url, className }: { url: string; className: string }) {
  const [src, setSrc] = useState(url);
  useEffect(() => setSrc(url), [url]);
  return (
    <img
      src={src}
      alt=""
      className={`object-contain select-none ${className}`}
      style={{ width: '1em', height: '1em' }}
      draggable={false}
      loading="eager"
      decoding="async"
      onError={() => {
        if (src === url) setSrc(`${url}${url.includes('?') ? '&' : '?'}retry=1`);
      }}
    />
  );
}

export function Picture({
  media,
  className = '',
  fallback,
  ground = false,
}: {
  media: MediaRef | undefined;
  className?: string;
  /** Shown instead of the bare "?" when the word has no illustration (abstract words). */
  fallback?: ReactNode;
  /** Cast a contact shadow on the ground under the artwork (visual plan §1.2). */
  ground?: boolean;
}) {
  // `.grounded` draws its ellipse with `::after`, and generated content never renders on a
  // replaced element — so on the `<img>` this returns for a real illustration the shadow simply
  // did not exist, which is every word that has artwork. The wrapper carries the class (and the
  // font-size the ellipse is sized against); the art inside is 1em either way.
  const size = ground ? 'text-[1em]' : className;
  const art =
    media === undefined ? (
      fallback !== undefined ? (
        <>{fallback}</>
      ) : (
        <div className={`text-muted ${size}`}>?</div>
      )
    ) : media.kind === 'emoji' ? (
      <span className={`leading-none select-none ${size}`} role="img">
        {media.value}
      </span>
    ) : media.kind === 'url' ? (
      <UrlPicture url={media.url} className={size} />
    ) : null;
  if (!ground) return art;
  return <span className={`grounded inline-flex ${className}`}>{art}</span>;
}

/** A picture-sized tile with the word's hanzi in it, for abstract words that have no
 * illustration: it keeps the card grid aligned and reads as deliberate rather than broken. */
export function HanziTile({ text, className = '' }: { text: string; className?: string }) {
  return (
    <span
      className={`flex items-center justify-center rounded-[0.18em] bg-brand-soft text-brand ${className}`}
      style={{ width: '1em', height: '1em' }}
      role="img"
    >
      <Hanzi
        className={
          text.length > 2 ? 'text-[0.3em]' : text.length > 1 ? 'text-[0.42em]' : 'text-[0.6em]'
        }
      >
        {text}
      </Hanzi>
    </span>
  );
}

export function Hanzi({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`hanzi ${className}`}>{children}</span>;
}

/** A word shown next to the lesson character it's teaching (e.g. 衣服 for 衣, 耳朵 for 耳): school
 * textbooks teach the single character, but nobody says it on its own in spoken Mandarin, so the
 * screen shows the real two-syllable word with the character being taught inked in at full weight
 * and its neighbour(s) greyed back, rather than either hiding the word or presenting both
 * characters as equally new. Words that already equal their character (鱼, 雨, 牙) render plainly. */
export function WordHanzi({
  text,
  target,
  className = '',
}: {
  text: string;
  target: string;
  className?: string;
}) {
  if (text === target || !text.includes(target)) return <Hanzi className={className}>{text}</Hanzi>;
  return (
    <Hanzi className={className}>
      {[...text].map((c, i) => (
        <span key={i} className={c === target ? 'hanzi-target' : 'hanzi-rest'}>
          {c}
        </span>
      ))}
    </Hanzi>
  );
}

export function showPinyin(mode: PinyinMode, teaching: boolean): boolean {
  if (mode === 'always') return true;
  if (mode === 'teach_only') return teaching;
  return false;
}

export function SpeakerButton({
  media,
  autoPlay = true,
  size = 'lg',
  onPlay,
}: {
  media: MediaRef | undefined;
  autoPlay?: boolean;
  /** `xs` scales with `dvh` rather than a fixed size, for a control repeated several times down
   * a column (the practice passage's per-sentence speaker) where a fixed 80px box is fine on a
   * tall iPad but overflows a short landscape window (`docs/missions/spec.md` §2.1's no-scroll
   * rule, caught on the 1440×820 desktop e2e project). */
  size?: 'lg' | 'sm' | 'xs';
  /** Listening questions route every play through their own prompt-evidence tracker. */
  onPlay?: () => Promise<PlayOutcome>;
}) {
  const [silent, setSilent] = useState(false);
  // Held in a ref so a parent's inline callback does not re-run the mount effect and replay the
  // clip on every render.
  const play = useRef(onPlay);
  play.current = onPlay;
  const attempt = useRef(0);
  const say = useCallback(() => {
    const ticket = ++attempt.current;
    void (play.current ? play.current() : audio.play(media)).then((outcome) => {
      if (ticket !== attempt.current) return;
      // 'skipped' is another clip taking the channel, which reports for itself — and this
      // button's ticket only knows about replays it issued, so it cannot tell that case from
      // silence. Calling it silence lights "No sound: tap to try again" over a clip that is
      // audibly playing: `onCorrectShown` replays the word the moment the child answers, so
      // Count's hear variant raised the alarm exactly when they got it right.
      if (outcome.status === 'skipped') return;
      setSilent(wentSilent(outcome));
    });
  }, [media]);
  useEffect(() => {
    setSilent(false);
    if (autoPlay) say();
    return () => {
      attempt.current++;
      audio.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);
  // p-0 drops .btn's 2rem side padding so the icon gets the whole circle; without it the small
  // button leaves the icon half a rem of room and flex-shrinks it to a sliver. `xs` also drops
  // .btn's 4.5rem min size, which otherwise wins over its height and keeps it at 72px everywhere.
  const cls =
    size === 'lg'
      ? 'h-40 w-40'
      : size === 'sm'
        ? 'h-20 w-20'
        : 'h-[clamp(2.25rem,6.5dvh,3.5rem)] w-[clamp(2.25rem,6.5dvh,3.5rem)] min-h-0 min-w-0';
  const iconCls =
    size === 'lg'
      ? 'h-16 w-16'
      : size === 'sm'
        ? 'h-10 w-10'
        : 'h-[clamp(1.1rem,3.2dvh,1.75rem)] w-[clamp(1.1rem,3.2dvh,1.75rem)]';
  return (
    <button
      type="button"
      aria-label={silent ? 'No sound: tap to try again' : 'Play'}
      data-testid="speaker"
      data-silent={silent || undefined}
      className={`btn btn-primary relative rounded-full p-0 ${cls}`}
      onClick={say}
    >
      <SpeakerIcon className={`${iconCls} text-white`} />
      {/* The app's own "tap to hear" signal, put on the one control that can actually fix this:
          a clip the browser refused is refused for want of a gesture, and this is a gesture. */}
      {silent && <span className="ripple pointer-events-none absolute inset-0 rounded-full" />}
    </button>
  );
}

/**
 * Offered when a listening prompt did not play: the browser refused it, it failed to load, or it
 * ran out of time. Silence used to be indistinguishable from a clip that had played, so a child
 * sat in front of a question with nothing to hear and no sign that anything was wrong — and a
 * guess made in that silence was scored as a listening mistake. This says what happened and
 * gives them the one thing that can fix it, which is a tap of their own: a refused clip is
 * refused for want of a gesture, and this is a gesture.
 */
export function SilentPrompt({ onReplay }: { onReplay: () => void }) {
  return (
    <button
      type="button"
      className="btn btn-primary rounded-full px-5 py-2 text-lg"
      data-testid="silent-prompt"
      onClick={onReplay}
    >
      🔇 No sound — tap to hear it
    </button>
  );
}

/** n filled stars, sized to the surrounding text (use with a text-size utility on the parent). */
export function Stars({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="inline-flex items-center" aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <StarIcon key={i} className="inline-block h-[1em] w-[1em] align-[-0.15em] text-accent" />
      ))}
    </span>
  );
}

/** Friendly full-runner loading placeholder: Shizi thinking + three bouncing dots. Used wherever
 * a runner screen would otherwise show a bare "Loading…"/"Saving…" string. */
export function Loader() {
  return (
    <div
      className="runner flex flex-col items-center justify-center gap-3 text-muted"
      data-testid="loader"
    >
      <Shizi size={100} pose="think" />
      <div className="flex items-center gap-2 text-xl">
        <span>One moment…</span>
        <span className="flex items-center gap-1" aria-hidden>
          <span className="loader-dot" />
          <span className="loader-dot" />
          <span className="loader-dot" />
        </span>
      </div>
    </div>
  );
}

/**
 * The area an exercise plays in: a grid that fills the runner's `main` rather than a band of
 * content centred in it with a third of the screen left bare (visual plan §1.1).
 *
 * The wrapper only guarantees the height is taken; a format still says what its rows are, and
 * — the part that actually won the space back — its play pane must *stretch* inside its track
 * (`place-items`/`content-*` stretch, sizes in `dvh`) rather than centre a fixed-size cluster.
 */
export function Board({
  children,
  className = '',
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div className={`grid h-full min-h-0 w-full ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/** 田字格: the writing grid every P1 exercise book uses. Children draw on top of it. */
export function WritingGrid({
  children,
  className = '',
  state,
  testId,
  ...rest
}: {
  children?: ReactNode;
  className?: string;
  /** ok rings the square when the character is finished. */
  state?: 'ok' | undefined;
  testId?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`tianzige ${state === 'ok' ? 'ring-4 ring-ok' : ''} ${className}`}
      data-testid={testId}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Palette a burst can be tinted with: green for a win, amber for a beat, blue for water. */
export type BurstTone = 'ok' | 'accent' | 'water';
const BURST_COLOURS: Record<BurstTone, string[]> = {
  ok: ['var(--color-ok)', 'var(--color-accent)', '#8fd8b0'],
  accent: ['var(--color-accent)', '#ffd88a', 'var(--color-accent-deep)'],
  water: ['#bfe5ee', '#ffffff', '#7fc8d8'],
};

/**
 * A short burst of particles at the point of the action — the generalised form of Feed Shizi's
 * crumbs, so "you got it" reads the same everywhere (visual plan §1.5). Absolutely positioned:
 * put it inside a `relative` box centred on whatever was just achieved.
 */
export function Burst({ tone = 'ok', n = 8 }: { tone?: BurstTone; n?: number }) {
  const colours = BURST_COLOURS[tone];
  return (
    <span className="burst pointer-events-none absolute inset-0" aria-hidden data-testid="burst">
      {Array.from({ length: n }, (_, i) => {
        const a = (i / n) * Math.PI * 2;
        return (
          <span
            key={i}
            style={{
              ['--bx' as string]: `${Math.cos(a) * 56}px`,
              ['--by' as string]: `${Math.sin(a) * 56 - 10}px`,
              background: colours[i % colours.length],
              animationDelay: `${i * 18}ms`,
            }}
          />
        );
      })}
    </span>
  );
}

export function Tile({
  state,
  onClick,
  children,
  testId,
  className = '',
}: {
  state?: 'correct' | 'wrong' | 'dim' | undefined;
  onClick: () => void;
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`tile ${className}`}
      data-state={state}
      data-testid={testId}
      onClick={onClick}
      disabled={state === 'wrong' || state === 'dim'}
    >
      {children}
    </button>
  );
}

export function ProgressDots({
  index,
  total,
  results,
}: {
  index: number;
  total: number;
  results: boolean[];
}) {
  const dots = Array.from({ length: total }, (_, i) => i);
  return (
    <div
      className="flex gap-1.5 flex-wrap justify-center"
      aria-label={`question ${index + 1} of ${total}`}
    >
      {dots.map((i) => {
        const r = results[i];
        const cls =
          r === true ? 'bg-ok' : r === false ? 'bg-bad' : i === index ? 'bg-brand' : 'bg-black/10';
        return <span key={i} className={`h-2.5 w-2.5 rounded-full ${cls}`} />;
      })}
    </div>
  );
}

/**
 * The written instruction for the current exercise type, in English. Nothing is spoken
 * automatically: the only audio a question plays by itself is the Chinese word, so there is
 * never a spoken sentence to confuse with the thing to say or find. Tapping the pill reads the
 * English instruction aloud (generated clip, else browser speech).
 */
export function InstructionBar({ bundle, id }: { bundle: LessonBundle; id: string }) {
  const ins: Instruction | undefined = bundle.instructions[id];
  const [speaking, setSpeaking] = useState(false);
  if (!ins) return null;
  const media = ins.audio.en
    ? bundle.media[ins.audio.en]
    : bundle.media[`tts:instruction:${ins.id}:en`];
  return (
    <button
      type="button"
      className={`flex items-center gap-3 rounded-full px-4 py-2 text-lg text-muted transition-colors ${
        speaking ? 'bg-brand-soft text-ink' : 'bg-white/70'
      }`}
      data-testid="instruction"
      data-speaking={speaking || undefined}
      onClick={() => {
        setSpeaking(true);
        void audio.announce([media]).finally(() => setSpeaking(false));
      }}
    >
      <span aria-hidden>💬</span>
      <span>{ins.text.en}</span>
    </button>
  );
}

/** How long the flash verdict takes to fade out; the CSS below uses the same number. */
const FLASH_FADE_MS = 260;

export function Feedback({
  correct,
  reveal,
  retry = false,
  own = false,
  flash = null,
  onDone,
}: {
  correct: boolean;
  reveal: ReactNode;
  /** A wrong answer with a go left: the button re-opens the question instead of moving on. */
  retry?: boolean;
  /** The exercise already shows the outcome itself (a finished character, a lit-up picture):
   * keep it visible under a bottom bar rather than dimming it behind a card. */
  own?: boolean;
  /**
   * Say it and get out of the way, instead of waiting for a tap. The Chinese line (太棒了！ /
   * 再试一次。) plays with it. For the run-of-asks games — sort a card, slide a tone — a button
   * between every single answer is a tax on the child rather than a beat: there is nothing to
   * read, the board has already shown what happened, and another ask is coming straight away.
   */
  flash?: { say: MediaRef | undefined; zh: string; en: string } | null;
  onDone: () => void;
}) {
  // The tap-to-continue shapes never auto-dismiss: there is always time to read what just
  // happened before it's gone. `flash` is the exception, and it says so.
  useEffect(() => {
    if (correct) sfx.correct();
    else sfx.wrong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const done = useRef(false);
  const finish = () => {
    if (done.current) return;
    done.current = true;
    onDone();
  };
  const [leaving, setLeaving] = useState(false);
  // The pill holds for as long as the line takes to say, not for a number picked in advance:
  // the clips run 1.8s to 2.2s, and a fixed 1.3s cut 太棒了 off in the middle. A floor keeps it
  // readable when the clip is missing or already cached, a ceiling keeps a slow fetch from
  // parking the game, and then it fades out under its own animation before the turn ends.
  useEffect(() => {
    if (!flash) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const after = (ms: number) => new Promise((r) => timers.push(setTimeout(r, ms)));
    const leave = () => {
      if (done.current) return;
      setLeaving(true);
      timers.push(setTimeout(finish, FLASH_FADE_MS));
    };
    void Promise.all([audio.announce([flash.say]), after(1100)]).then(leave);
    void after(2600).then(leave);
    return () => {
      for (const t of timers) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (flash)
    return (
      <div
        className="absolute inset-0 z-40 flex items-center justify-center"
        data-testid="feedback"
        data-verdict={correct ? 'correct' : retry ? 'retry' : 'wrong'}
        data-style="flash"
        onClick={finish}
      >
        <div
          className={`${leaving ? 'flash-leave' : 'flash-in'} flex flex-col items-center gap-1 rounded-[2rem] px-10 py-5 text-white shadow-xl ${correct ? 'bg-ok' : 'bg-bad'}`}
        >
          <span className="hanzi text-[clamp(2rem,6dvh,3.5rem)] leading-none font-bold">
            {flash.zh}
          </span>
          <span className="text-[clamp(1rem,2.6dvh,1.5rem)] font-bold opacity-90">{flash.en}</span>
        </div>
      </div>
    );
  const label = correct ? 'Next ✓' : 'Next →';
  const pill = (
    <div
      className={`pop flex items-center gap-3 rounded-full px-8 py-3 text-3xl font-bold text-white ${correct ? 'bg-ok' : 'bg-bad'}`}
    >
      <span>{correct ? '✓' : '✗'}</span>
      <span>{correct ? 'Great!' : 'Not quite'}</span>
    </div>
  );
  const button = (
    <button
      type="button"
      data-testid="feedback-next"
      className="btn btn-primary text-2xl"
      onClick={onDone}
    >
      {label}
    </button>
  );
  const cornerButton = (
    <button
      type="button"
      data-testid="feedback-next"
      className={`pop btn pointer-events-auto text-2xl text-white ${correct ? 'bg-ok' : 'bg-bad'}`}
      onClick={onDone}
    >
      {correct ? '✓ ' : '✗ '}
      {correct ? 'Great! Next' : 'Not quite. Next'}
    </button>
  );
  // A retry is the one verdict the child has to *act* on rather than read past, so its button
  // sits in the middle of the board rather than in a corner: after a mistake the thing to do
  // next should be where the eyes already are, not somewhere to be hunted for. No scrim under
  // it — the board behind is still showing the right answer (Bubble Lagoon outlines the bubble
  // that was called, Boat Forks lights the sign), and that is what the child should be looking
  // at while the button waits.
  if (retry)
    return (
      <div
        className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center px-6"
        data-testid="feedback"
        data-verdict="retry"
        data-style="center"
      >
        <button
          type="button"
          data-testid="feedback-next"
          className="pop btn pointer-events-auto bg-bad px-10 text-3xl text-white shadow-[0_8px_0_0_rgba(30,36,51,0.18)] ring-4 ring-white/80"
          onClick={onDone}
        >
          ↻ Try again
        </button>
      </div>
    );
  // Visual plan §1.5: the corner button is the *default* and the scrim the exception. A grey
  // sheet over the whole board hid the thing the child had just done, and it was the same
  // moment whether they popped the last bubble of a lagoon or mis-tapped a tile.
  //
  // Only the answer reveal — the second wrong try, where the board has to be dimmed so the
  // right answer reads — still gets the scrim. A right answer keeps its board, and anything
  // the runner wanted to show alongside it (the word, its meaning) docks at the bottom rather
  // than covering the work.
  const scrim = !correct && !!reveal && !own;
  if (!scrim)
    return (
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-40 flex items-end justify-between gap-4 px-6 pb-6"
        data-testid="feedback"
        data-verdict={correct ? 'correct' : 'wrong'}
        data-style="corner"
      >
        {!own && correct && reveal ? (
          <div className="pop pointer-events-auto flex flex-1 items-center justify-center gap-4 rounded-3xl bg-white/95 px-6 py-3 text-center shadow-lg">
            {reveal}
          </div>
        ) : (
          <span />
        )}
        {cornerButton}
      </div>
    );
  return (
    <div
      className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-6 bg-black/40 px-6 text-center"
      data-testid="feedback"
      data-verdict="wrong"
      data-style="scrim"
    >
      {pill}
      <div className="pop flex flex-col items-center gap-2 rounded-3xl bg-white px-8 py-6 shadow-lg">
        {reveal}
      </div>
      {button}
    </div>
  );
}

export function Screen({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mx-auto flex min-h-dvh w-full max-w-5xl flex-col px-6 py-6 ${className}`}>
      {children}
    </div>
  );
}
